import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Label, Popup } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, CustomerModal, PreferredCommunicationChannel, ReactTable, useSelectedDealer, useSelectedLocation } from "components";
import { usePermanentRealTimeUser, useRegisterNavAction } from "hooks";
import { ROLES } from "models";
import { setFilters, useCustomersFilters } from "modules/CustomersDashboard/components/CustomerFilters/hooks";
import "modules/CustomersDashboard/components/CustomersTable/CustomersTable.scss";
import { useCustomersTableHandlers } from "modules/CustomersDashboard/components/CustomersTable/hooks";
import { setSelectedCustomer, useDeleteCustomerMutation, useListCustomersQuery, useSelectedCustomer } from "modules/CustomersDashboard/hooks";
import { PAGE_SIZE } from "utils/common";

import type { Customer } from "models";

const columnHelper = createColumnHelper<Customer>();

export const CustomersTable = () => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { filters } = useCustomersFilters();
  const { selectedCustomer } = useSelectedCustomer();
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();

  const { listCustomersData: customers, isFetchingCustomersList, refetchListCustomers } = useListCustomersQuery();
  const { handlePaginationChange, handlePageSizeChange } = useCustomersTableHandlers();
  const { mutate: deleteCustomer, isPending: isCustomerDeletePending, isSuccess: isCustomerDeleteSuccess } = useDeleteCustomerMutation();

  const [customerIdToDelete, setCustomerIdToDelete] = useState<number | null>(null);

  useRegisterNavAction(() => {
    setFilters({ ...filters, search_term: "" });
    refetchListCustomers();
  });

  useEffect(() => {
    if (isCustomerDeleteSuccess) setCustomerIdToDelete(null);
  }, [isCustomerDeleteSuccess]);

  const handleOpenConfirmDeleteCustomer = (customerId: number) => setCustomerIdToDelete(customerId);
  const handleCloseConfirmDeleteCustomer = () => setCustomerIdToDelete(null);

  const handleCloseCustomerModal = () => setSelectedCustomer(null);
  const handleOnRowClick = (customer: Customer) => setSelectedCustomer(customer);

  const handleDeleteCustomerConfirm = () => {
    if (!customerIdToDelete) return;

    deleteCustomer({ id: customerIdToDelete });
  };

  const handleOpenConfirmDeleteCustomerModal = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    e.stopPropagation();
    handleOpenConfirmDeleteCustomer(id);
  };

  const { page = 1, page_size: pageSize = PAGE_SIZE.PageSize25 } = filters;

  const pageCount = customers?.nb_pages || 0;
  const totalItems = customers?.nb_items || 0;
  const tablePagination = pageCount > 1 ? { pageIndex: page - 1, pageSize } : undefined;

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "dms_nr",
        header: () => <span> {t("v8_dms", "DMS")}# </span>,
        size: 250,
        cell: ({
          row: {
            original: { dms_nr }
          }
        }) => <Popup trigger={<div className="text-ellipsis column-size-250">{dms_nr}</div>} content={dms_nr} />
      }),

      columnHelper.accessor(row => row, {
        id: "name",
        header: () => <span> {t("v8_name", "Name")} </span>,
        size: 150,
        cell: ({
          row: {
            original: { title, firstname, surname }
          }
        }) => {
          const name = `${title || ""} ${firstname || ""} ${surname || ""}`.trim();
          return <Popup trigger={<div className="text-ellipsis column-size-150">{name}</div>} content={name} />;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "companyName",
        header: () => <span> {t("v8_company_name", "Company Name")} </span>,
        size: 150,
        cell: ({
          row: {
            original: { is_lease_company, company }
          }
        }) => (
          <Popup
            trigger={
              <div className="company-name-container column-size-150">
                {is_lease_company && (
                  <Label color="green">
                    <Icon className="building" />
                  </Label>
                )}
                <div className="text-ellipsis">{company}</div>
              </div>
            }
            content={company}
            disabled={!company}
          />
        )
      }),

      columnHelper.accessor(row => row, {
        id: "street",
        header: () => <span> {t("v8_street", "Street")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { street }
          }
        }) => <Popup trigger={<div className="text-ellipsis column-size-100">{street}</div>} content={street} />
      }),

      columnHelper.accessor(row => row, {
        id: "postcode",
        header: () => <span> {t("v8_postal_code", "Postal Code")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { postcode }
          }
        }) => <Popup trigger={<div className="text-ellipsis column-size-100">{postcode}</div>} content={postcode} />
      }),

      columnHelper.accessor(row => row, {
        id: "place",
        header: () => <span> {t("v8_location", "Location")} </span>,
        size: 150,
        cell: ({
          row: {
            original: { place }
          }
        }) => <Popup trigger={<div className="text-ellipsis column-size-150">{place}</div>} content={place} />
      }),

      columnHelper.accessor(row => row, {
        id: "email_private",
        header: () => <span> {t("v8_email", "Email")} </span>,
        size: 250,
        cell: ({
          row: {
            original: { email_private }
          }
        }) => <Popup trigger={<div className="text-ellipsis column-size-250">{email_private}</div>} content={email_private} />
      }),

      columnHelper.accessor(row => row, {
        id: "tel_mobile_private",
        header: () => <span> {t("v8_phone", "Phone")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { tel_mobile_private }
          }
        }) => <Popup trigger={<div className="text-ellipsis column-size-100">{tel_mobile_private}</div>} content={tel_mobile_private} />
      }),

      columnHelper.accessor(row => row, {
        id: "preferred_communication_channel",
        header: () => <span> {t("v8_preferred_communication_channel", "Preferred Communication Channel")} </span>,
        size: 150,
        cell: ({
          row: {
            original: { preferred_communication_channel }
          }
        }) => <PreferredCommunicationChannel channel={preferred_communication_channel} />
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        size: 50,
        cell: ({
          row: {
            original: { id }
          }
        }) => {
          if (user?.role_id !== ROLES.SuperAdmin) return;

          return (
            <Button basic icon className="delete-btn" onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleOpenConfirmDeleteCustomerModal(e, id)}>
              <Icon className="trash" color="red" />
            </Button>
          );
        }
      })
    ],
    [t, user?.role_id, handleOpenConfirmDeleteCustomerModal]
  );

  return (
    <>
      <div className="CustomersTable">
        <ReactTable
          isFetching={isFetchingCustomersList}
          columns={columns}
          data={customers?.items}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          pagination={tablePagination}
          onPaginationChange={handlePaginationChange}
          onChangePageSize={handlePageSizeChange}
          onRowClick={(customer: Customer) => handleOnRowClick(customer)}
        />

        <CustomConfirm
          type={CUSTOM_CONFIRM_TYPES.Danger}
          isOpen={!!customerIdToDelete}
          handleCancel={handleCloseConfirmDeleteCustomer}
          confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
          handleConfirm={handleDeleteCustomerConfirm}
          isLoading={isCustomerDeletePending}
        />
      </div>
      {selectedCustomer && (
        <CustomerModal customer={selectedCustomer} dealer={selectedDealer} location={selectedLocation} isOpen={true} onClose={handleCloseCustomerModal} />
      )}
    </>
  );
};
